import { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { gsap } from "gsap";
import shirtImg from "../../assets/newshirt2.jpeg";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&family=Playfair+Display:ital,wght@1,400;1,600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #040008; overflow-x: hidden; }
`;

// ── Keyframes
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-2deg); }
  50%       { transform: translateY(-16px) rotate(2deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50%       { opacity: 0.7; transform: scale(1.06); }
`;

const scanDown = keyframes`
  0%   { transform: translateY(-100%); opacity: 0; }
  5%   { opacity: 0.4; }
  95%  { opacity: 0.4; }
  100% { transform: translateY(800%); opacity: 0; }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
`;

const grain = keyframes`
  0%   { transform: translate(0, 0); }
  10%  { transform: translate(-2%, -3%); }
  30%  { transform: translate(3%, -1%); }
  50%  { transform: translate(-1%, 3%); }
  70%  { transform: translate(2%, 1%); }
  90%  { transform: translate(-3%, 2%); }
  100% { transform: translate(0, 0); }
`;

// ── Page
const Page = styled.div`
  min-height: 100vh;
  background: #040008;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Grain = styled.div`
  position: fixed;
  inset: -50%;
  width: 200%;
  height: 200%;
  pointer-events: none;
  opacity: 0.045;
  z-index: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  animation: ${grain} 8s steps(1) infinite;
`;

const Orb = styled.div`
  position: fixed;
  border-radius: 50%;
  filter: blur(${({ blur }) => blur || "120px"});
  pointer-events: none;
  opacity: ${({ op }) => op || 0.15};
  width: ${({ s }) => s};
  height: ${({ s }) => s};
  background: ${({ c }) => c};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
  animation: ${pulse} ${({ dur }) => dur || "5s"} ease-in-out infinite;
  animation-delay: ${({ del }) => del || "0s"};
`;

const ScanLine = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(168, 85, 247, 0.3),
    transparent
  );
  pointer-events: none;
  z-index: 5;
  animation: ${scanDown} 6s linear infinite;
`;

// ── Main content
const Main = styled.main`
  position: relative;
  z-index: 2;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(4rem, 8vh, 6rem) clamp(1.5rem, 6vw, 4rem);
  text-align: center;
  gap: clamp(2rem, 4vh, 3rem);
`;

// ── Back nav
const BackBtn = styled.button`
  position: fixed;
  top: 1.5rem;
  left: 1.5rem;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 100px;
  padding: 7px 14px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.72rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.35);
  cursor: pointer;
  transition:
    background 0.2s,
    color 0.2s,
    border-color 0.2s;
  opacity: 0;

  &:hover {
    background: rgba(168, 85, 247, 0.08);
    border-color: rgba(168, 85, 247, 0.25);
    color: rgba(255, 255, 255, 0.6);
  }
`;

// ── Tag
const Tag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.3);
  opacity: 0;
  transform: translateY(14px);
`;

const TagDot = styled.div`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #a855f7;
  box-shadow: 0 0 8px #a855f7;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

// ── Shirt visual
const ShirtWrap = styled.div`
  position: relative;
  opacity: 0;
  transform: translateY(30px) scale(0.95);
`;

const ShirtImg = styled.img`
  width: clamp(180px, 30vw, 300px);
  height: auto;
  object-fit: contain;
  display: block;
  animation: ${float} 5s ease-in-out infinite;
  filter: drop-shadow(0 30px 60px rgba(168, 85, 247, 0.35));
`;

const ShirtBadge = styled.div`
  position: absolute;
  top: -12px;
  right: -12px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e879f9, #a855f7);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  box-shadow: 0 0 24px rgba(232, 121, 249, 0.5);
  animation: ${pulse} 2s ease-in-out infinite;
`;

const ShirtBadgeText = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.45rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #fff;
  line-height: 1.2;
  text-align: center;
`;

// ── Headline
const Headline = styled.h1`
  font-family: "Bebas Neue", sans-serif;
  font-size: clamp(4rem, 14vw, 13rem);
  line-height: 0.88;
  letter-spacing: 0.02em;
  color: #fff;
  opacity: 0;
  transform: translateY(30px);

  .line2 {
    display: block;
    background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed, #e879f9);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 4s linear infinite;
  }
`;

// ── Sub copy
const Sub = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: clamp(1rem, 1.8vw, 1.15rem);
  font-weight: 300;
  color: rgba(255, 255, 255, 0.38);
  line-height: 1.8;
  max-width: 480px;
  opacity: 0;
  transform: translateY(20px);

  em {
    font-style: italic;
    color: rgba(255, 255, 255, 0.6);
  }
`;

// ── Detail chips
const ChipRow = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  justify-content: center;
  opacity: 0;
  transform: translateY(16px);
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-family: "DM Sans", sans-serif;
  font-size: 0.7rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 0.04em;
`;

// ── CTA button
const CtaWrap = styled.div`
  opacity: 0;
  transform: translateY(20px);
`;

const CtaBtn = styled.button`
  position: relative;
  padding: 0 clamp(2rem, 5vw, 3.5rem);
  height: clamp(52px, 7vh, 62px);
  border-radius: 100px;
  border: none;
  cursor: pointer;
  font-family: "DM Sans", sans-serif;
  font-size: clamp(0.88rem, 1.4vw, 1rem);
  font-weight: 500;
  letter-spacing: 0.08em;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #a855f7, #c026d3);
  background-size: 200% auto;
  box-shadow:
    0 8px 32px rgba(124, 58, 237, 0.45),
    0 0 0 1px rgba(255, 255, 255, 0.08) inset;
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    background-position 0.4s;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 12%;
    right: 12%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.35),
      transparent
    );
    border-radius: 50%;
  }

  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow:
      0 18px 50px rgba(124, 58, 237, 0.55),
      0 0 0 1px rgba(255, 255, 255, 0.1) inset;
    background-position: right center;
  }

  &:active {
    transform: scale(0.97);
  }
`;

const ArrowSpan = styled.span`
  display: inline-block;
  margin-left: 8px;
  transition: transform 0.25s ease;

  ${CtaBtn}:hover & {
    transform: translateX(4px);
  }
`;

// ── Small disclaimer
const Disclaimer = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.65rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.15);
  line-height: 1.6;
  opacity: 0;

  span {
    display: inline-block;
    width: 4px;
    height: 0.7em;
    background: rgba(168, 85, 247, 0.6);
    vertical-align: middle;
    margin-left: 3px;
    animation: ${blink} 1s step-end infinite;
  }
`;

// ── Component
export default function TshirtPage() {
  const navigate = useNavigate();

  const backRef = useRef(null);
  const tagRef = useRef(null);
  const shirtRef = useRef(null);
  const headlineRef = useRef(null);
  const subRef = useRef(null);
  const chipRef = useRef(null);
  const ctaRef = useRef(null);
  const disclaimerRef = useRef(null);

  useEffect(() => {
    gsap
      .timeline({ delay: 0.05 })
      .to(backRef.current, { opacity: 1, duration: 0.5, ease: "power2.out" }, 0)
      .to(
        tagRef.current,
        { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" },
        0.1,
      )
      .to(
        shirtRef.current,
        { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "back.out(1.4)" },
        0.15,
      )
      .to(
        headlineRef.current,
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" },
        0.25,
      )
      .to(
        subRef.current,
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" },
        0.38,
      )
      .to(
        chipRef.current,
        { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" },
        0.48,
      )
      .to(
        ctaRef.current,
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out" },
        0.55,
      )
      .to(
        disclaimerRef.current,
        { opacity: 1, duration: 0.4, ease: "power2.out" },
        0.68,
      );
  }, []);

  return (
    <>
      <GlobalStyle />
      <Page>
        <Grain />
        <ScanLine />
        <Orb
          s="500px"
          c="rgba(124,58,237,0.22)"
          top="-10%"
          left="-8%"
          op={0.22}
          dur="6s"
          del="0s"
          blur="130px"
        />
        <Orb
          s="400px"
          c="rgba(232,121,249,0.18)"
          bottom="-5%"
          right="-6%"
          op={0.18}
          dur="7s"
          del="1.5s"
          blur="110px"
        />
        <Orb
          s="300px"
          c="rgba(168,85,247,0.15)"
          top="45%"
          left="42%"
          op={0.12}
          dur="5.5s"
          del="0.8s"
          blur="100px"
        />

        <BackBtn ref={backRef} onClick={() => navigate(-1)}>
          ← back
        </BackBtn>

        <Main>
          <Tag ref={tagRef}>
            <TagDot />
            dropping soon
          </Tag>

          <ShirtWrap ref={shirtRef}>
            <ShirtImg
              src={shirtImg}
              alt="NudR branded t-shirt"
              draggable="false"
            />
            <ShirtBadge>
              <ShirtBadgeText>
                limited
                <br />
                drop
              </ShirtBadgeText>
            </ShirtBadge>
          </ShirtWrap>

          <Headline ref={headlineRef}>
            THE NUDR SHIRT
            <br />
            <span className="line2">IS COMING.</span>
          </Headline>

          <Sub ref={subRef}>
            yeah, we're making a shirt. <em>a special one.</em>
            <br />
            unisex, heavy weight, NudR logo, and for the cool ones.
            <br />
            limited first drop!
          </Sub>

          <ChipRow ref={chipRef}>
            <Chip>🖤 unisex fit</Chip>
            <Chip>🧵 heavyweight cotton</Chip>
            <Chip>🔒 special run</Chip>
            <Chip>📦 Shipping across all US</Chip>
          </ChipRow>

          <CtaWrap ref={ctaRef}>
            <CtaBtn onClick={() => navigate("/waitlist")}>
              join the waitlist
              <ArrowSpan>→</ArrowSpan>
            </CtaBtn>
          </CtaWrap>

          <Disclaimer ref={disclaimerRef}>
            no price yet. no date yet. just first dibs.
            <span />
          </Disclaimer>
        </Main>
      </Page>
    </>
  );
}
